import useApi, {
    type Test,
    type TestPreview,
    type TestQuestion,
    type TestResult,
} from "./tests-api.ts";
import NiceModal, { type NiceModalHocProps, useModal } from "@ebay/nice-modal-react";
import type { FunctionComponent } from "preact";
import Modal, { type WizardElement, type WizardStep } from "./components/Modal.tsx";
import { useEffect, useRef, useState } from "preact/hooks";
import * as stylex from "@stylexjs/stylex";
import asObjectURL from "./utils/asObjectURL.ts";
import Button from "./components/Button.tsx";
import CurrencyDisplay from "./components/CurrencyDisplay.tsx";
import Spinner from "./components/Spinner.tsx";
import { useImmer } from "use-immer";
import GachaDialog from "./GachaDialog.tsx";
import Form from "./components/Form.tsx";

const styles = stylex.create({
    testInfo: {
        height: "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        alignItems: "center",
    },
    testInfoImage: {
        width: "80%",
        borderRadius: 6,
        aspectRatio: "3/2",
    },
    testInfoBaseContainer: {
        gap: 12,
        width: "80%",
        display: "flex",
        flexDirection: "column",
    },
    testInfoTitle: {
        display: "block",
        textAlign: "center",
        fontSize: 22,
        fontWeight: 600,
        fontFamily: "'Inter Variable', sans-serif",
        color: "#444",
    },
    testInfoDescription: {
        display: "block",
        textAlign: "center",
        fontSize: 14,
        fontFamily: "'Inter Variable', sans-serif",
        color: "#666",
    },
    testInfoActions: {
        gap: 10,
        width: "80%",
        margin: "auto",
        display: "grid",
        gridTemplateColumns: "repeat(2, 1fr)",
    },
    testInfoPriceContainer: {
        color: "#666",
        fontSize: 13,
        textAlign: "center",
        fontFamily: "'Inter Variable', sans-serif",
    },
    testInfoAuthorContainer: {
        gap: 5,
        display: "flex",
        alignItems: "center",
    },
    testInfoAuthorIcon: {
        width: 36,
        aspectRatio: 1,
        borderRadius: 4,
    },
    testInfoAuthorLink: {
        color: "#666",
        cursor: "pointer",
        textDecoration: "underline",
        fontFamily: "'Inter Variable', sans-serif",
    },
    testQuestionWrap: {
        gap: 12,
        height: "100%",
        display: "grid",
        gridTemplateRows: "240px 1fr",
    },
    testQuestionTitle: {
        display: "block",
        color: "#555",
        fontSize: 18,
        fontFamily: "'Inter Variable', sans-serif",
        borderBottomStyle: "dashed",
        borderBottomWidth: 1,
        borderBottomColor: "#CCC",
        paddingBottom: 4,
    },
    testQuestionContainer: {
        gap: 15,
        display: "flex",
        flexDirection: "column",
        textAlign: "center",
    },
    testQuestionImage: {
        backgroundColor: "#dae4ed",
        margin: "auto",
        height: "100%",
        width: "unset",
    },
    testQuestionOptions: {
        gap: 5,
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        maxHeight: 150,
        overflowY: "auto",
    },
    testQuestionRevertLink: {
        display: "block",
        fontSize: 14,
    },
    testQuestionCaption: {
        height: 160,
        color: "#333",
        fontSize: 14,
        display: "block",
        overflowY: "auto",
        fontFamily: "'Inter Variable', sans-serif",
    },
});

const TestInfoStep: FunctionComponent<{ partial: TestPreview; advance: () => void }> = ({
    partial,
    advance,
}) => {
    return (
        <div {...stylex.props(styles.testInfo)}>
            <img
                {...stylex.props(styles.testInfoImage)}
                src={asObjectURL(partial.cover)}
                alt={partial.title}
                draggable={false}
            />

            <div {...stylex.props(styles.testInfoBaseContainer)}>
                <span {...stylex.props(styles.testInfoTitle)}>{partial.title}</span>
                <span {...stylex.props(styles.testInfoDescription)}>{partial.description}</span>
                <div {...stylex.props(styles.testInfoActions)}>
                    <Button kind="success" label="Начать тест" onClick={advance} />
                    <Button
                        label="Купить полихром"
                        onClick={() => NiceModal.show(GachaDialog.GetKromer)}
                    />
                </div>
            </div>

            <div {...stylex.props(styles.testInfoPriceContainer)}>
                Цена:
                <CurrencyDisplay amount={partial.price!} />
                &nbsp;полихром(ов)
            </div>

            <div {...stylex.props(styles.testInfoPriceContainer, styles.testInfoAuthorContainer)}>
                Автор теста:
                <a
                    href={`https://openvk.org/id${partial.author!.id}?from=minitest`}
                    target="_blank"
                >
                    <img
                        {...stylex.props(styles.testInfoAuthorIcon)}
                        src={partial.author!.icon}
                        alt={partial.author!.name}
                    />
                </a>
                <a
                    {...stylex.props(styles.testInfoAuthorLink)}
                    href={`https://openvk.org/id${partial.author!.id}?from=minitest`}
                    target="_blank"
                >
                    {partial.author!.name}
                </a>
            </div>
        </div>
    );
};

interface TestQuestionProps {
    onOptionPicked: (result: string | null) => void;
    question: TestQuestion;
    advance: () => void;
    revert: () => void;
    isFirst: boolean;
    isLast: boolean;
}

const TestQuestionStep: FunctionComponent<TestQuestionProps> = ({
    question,
    onOptionPicked,
    advance,
    revert,
    isFirst,
    isLast,
}) => {
    const [picked, setPicked] = useState<string | null>(null);
    const pickOption = (result: string | null) => {
        onOptionPicked(result);
        setPicked(result);
    };

    return (
        <div {...stylex.props(styles.testQuestionWrap)}>
            <img
                {...stylex.props(styles.testInfoImage, styles.testQuestionImage)}
                src={asObjectURL(question.cover)}
                alt={question.title}
                draggable={false}
            />
            <div {...stylex.props(styles.testQuestionContainer)}>
                <span {...stylex.props(styles.testQuestionTitle)}>{question.title}</span>
                <div {...stylex.props(styles.testQuestionOptions)}>
                    {Object.entries(question.options).map(([optionId, optionLabel]) => (
                        <Form.Radio
                            name={question.id}
                            label={optionLabel}
                            checked={picked == optionId}
                            onChange={(e) => {
                                pickOption(e.currentTarget.checked ? optionId : null);
                            }}
                        />
                    ))}
                </div>
                <div>
                    {picked ? (
                        <Button
                            kind={isLast ? "success" : "default"}
                            label={isLast ? "Завершить тест" : "Перейти на следующий шаг"}
                            style={{ marginBottom: 8 }}
                            onClick={advance}
                        />
                    ) : null}

                    {!isFirst ? (
                        <span
                            {...stylex.props(
                                styles.testInfoAuthorLink,
                                styles.testQuestionRevertLink,
                            )}
                            onClick={revert}
                        >
                            Назад
                        </span>
                    ) : null}
                </div>
            </div>
        </div>
    );
};

const TestResultStep: FunctionComponent<{
    test: TestPreview;
    result: TestResult;
}> = ({ test, result }) => (
    <div {...stylex.props(styles.testQuestionWrap)}>
        <img
            {...stylex.props(styles.testInfoImage, styles.testQuestionImage)}
            src={asObjectURL(result.cover)}
            alt={result.title}
            draggable={false}
        />
        <div {...stylex.props(styles.testQuestionContainer)}>
            <span {...stylex.props(styles.testQuestionTitle)}>{result.title}</span>
            <span {...stylex.props(styles.testQuestionCaption)}>{result.caption}</span>
            <div>
                <Button
                    kind="primary"
                    label="Поделиться результатом"
                    onClick={() => {
                        if (test.draft) {
                            NiceModal.show(Modal.Alert, {
                                title: "Опубликуйте тест чтобы можно было делиться его результатами :3",
                            });
                            return;
                        }

                        Modal.useLoader("Сохранение результата", async () => {
                            const ref = await (
                                await useApi()
                            ).getResultShareablePictureRef(test.id, result.id);
                            try {
                                // @ts-ignore
                                const uid = (await VKAPI.getUser()).user.id;
                                const message = `МиниТест: ${test.title}\n${test.description}\n\n👉🏿Тест тут: https://openvk.org/app1?test=${test.id}`;

                                // @ts-ignore
                                const post = await VKAPI.execute("wall.post", {
                                    message,
                                    owner_id: uid,
                                    attachments: `photo${ref}`,
                                });
                            } catch (e) {
                                // @ts-ignore
                                if (e.error == "No permission to use this method") {
                                    throw new Error(
                                        "Вы не дали разрешение на публикацию записей от вашего имени",
                                    );
                                }

                                // @ts-ignore
                                throw new Error("Ошибка OVK API: " + e.error);
                            }
                        }).then(() => NiceModal.show(Modal.Alert, {
                            title: "Результат опубликован на вашей стене :3"
                        }));
                    }}
                />
            </div>
        </div>
    </div>
);

const calculateResult = (answers: { [key: string]: string | null }, results: TestResult[]) => {
    const ranking: [TestResult, number][] = [];
    results.forEach((result) => {
        let score = 0;
        for (const question in result.options) {
            const answer = answers[question];
            const expected = result.options[question];

            if (null == answer || null == expected || answer == expected) score++;
        }

        ranking.push([result, score]);
    });

    ranking.sort((a, b) => b[1] - a[1]);

    return ranking[0][0];
};

interface TestAppletProps {
    testPreview: TestPreview;
    acquireFullTest: () => Promise<Test>;
}

const TestApplet: FunctionComponent<NiceModalHocProps & TestAppletProps> = NiceModal.create(
    ({ testPreview, acquireFullTest }) => {
        const testInProgress = useRef<boolean>(false);
        const fullTest = useRef<Test | null>(null);
        const answers = useRef<{ [key: string]: string | null }>({});
        const wizard = useRef<WizardElement>(null);
        const modal = useModal();

        const [steps, setSteps] = useImmer<WizardStep[]>([
            ({ advance }) => <TestInfoStep advance={advance} partial={testPreview} />,
            () => <Spinner.Loader label="Загрузка теста" fullHeight={true} />,
        ]);

        const processResults = () => {
            const winningResult = calculateResult(answers.current, fullTest.current!.results);
            console.log(winningResult);
            setSteps((steps) => {
                steps.push(() => <TestResultStep test={testPreview} result={winningResult} />);
            });
        };

        const onStepChanged = async (step: number) => {
            // At the results loading screen
            if (testInProgress.current && step == steps.length - 1) {
                setTimeout(() => wizard.current!.advance(), 500);
                testInProgress.current = false;
                return void processResults();
            }

            if (1 != step) return;

            let full;
            try {
                full = await acquireFullTest();
            } catch (e) {
                await NiceModal.show(Modal.Alert, {
                    title: typeof e == "string" ? e : (e as Error).message,
                });

                return void modal.remove();
            }

            setSteps((steps) => {
                steps.push(
                    ...full.questions.map((question, q_idx) => {
                        // @ts-ignore
                        return ({ advance, revert }) => (
                            <TestQuestionStep
                                question={question}
                                advance={advance}
                                revert={revert}
                                isFirst={q_idx == 0}
                                isLast={q_idx == full.questions.length - 1}
                                onOptionPicked={(option) =>
                                    (answers.current![question.id] = option)
                                }
                            />
                        );
                    }),
                    () => <Spinner.Loader label="Получение результатов" fullHeight={true} />,
                );
            });

            fullTest.current = full;
        };

        useEffect(() => {
            if (1 == wizard.current!.currentStep) {
                testInProgress.current = true;
                wizard.current!.advance();
            }

            return () => {};
        }, [steps]);

        return (
            <Modal.Wizard
                ref={wizard}
                steps={steps}
                onStepChanged={onStepChanged}
                style={{ width: "95%", height: "95%" }}
                progressBarVisible={steps.length > 2}
                stepGeometry={{ width: 550, height: 540, containerMaxHeight: "unset" }}
            />
        );
    },
);

export default TestApplet;
